import { FC } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { EditNoteWithQueryParamDrawer } from '../edit-note/EditNoteWithQueryParamDrawer';
import { IAction, Menu } from '../../components/Menu';
import { DeleteIcon, DownloadIcon, EditIcon, LockSolidIcon, ThumbtackIcon, UnlockSolidIcon } from '../../theme/Icons';
import { NotesModel } from '../model/NotesModel';
import { useIonAlert } from '@ionic/react';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import dayjs from 'dayjs';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareMapState } from '../../caremap/CareMapState';

export const ViewNoteMenu: FC<{model: NotesModel, noteId: string, trackingFrom?: string, customEditHandler?: () => void, onCloseDrawer?: () => void}> = ({ model, noteId, trackingFrom, customEditHandler, onCloseDrawer }) => {
    const [presentAlert] = useIonAlert();
    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);
    const history = useInstance(HistoryViewModel);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    const notePinsState = notesFactory.notePinsState;
    useSubscription(() => notePinsState);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const note = model.getNote(noteId);
    const isPinned = notePinsState.isPinned(noteId);

    if (!note) {
        return;
    }

    const actions: IAction[] = [];

    if (model.canEdit(note)) {
        actions.push({
            Icon: EditIcon,
            text: `Edit Note`,
            'data-type': 'edit-note-button',
            handler: customEditHandler || (() => editNoteDrawer.open({ noteId: note.noteId, trackingFrom }))
        });
    }

    // a contributor edits their own entries but cannot publish them
    if (model.canEdit(note) && careMapState.hasPermissionTo('publish-note')) {
        // Per-note visibility, using the same "Shared with Viewers" toggle as the note form.
        // Saves immediately (no surrounding form here) and fires the same tracking event.
        const toggleVisibility = async (shared: boolean) => {
            const noteVM = await notesFactory.createEditForm(note.noteId);
            noteVM?.setNote({ ...note, isPrivate: !shared });
            await noteVM?.save();
            notesFactory.toastPresenter.toast({ duration: 3000, message: shared ? 'This note is now shared with viewers.' : 'This note is now hidden from viewers.' });
            mixpanel.track('Note Visibility Toggled', () => ({ 'State': shared ? 'shared' : 'private' }));
        };
        actions.push({
            Icon: note.isPrivate ? LockSolidIcon : UnlockSolidIcon,
            text: note.isPrivate ? 'Share with Viewers' : 'Hide from Viewers',
            'data-type': note.isPrivate ? 'share-with-viewers-button' : 'hide-from-viewers-button',
            handler: () => toggleVisibility(Boolean(note.isPrivate))
        });
    }

    // Pin/Unpin action (only for signed-in users who have the pin link)
    if (note.links?.pin) {
        actions.push({
            Icon: ThumbtackIcon,
            text: isPinned ? 'Unpin Note' : 'Pin Note',
            'data-type': isPinned ? 'unpin-note-button' : 'pin-note-button',
            handler: async () => {
                await notePinsState[isPinned ? 'unpinNote' : 'pinNote']({ noteId, pinLink: note.links.pin });
                notesFactory.tracking[isPinned ? 'onNoteUnpinned' : 'onNotePinned'](note, trackingFrom);
                notesFactory.toastPresenter.toast({ duration: 3000, message: isPinned ? 'Note unpinned!' : 'Note pinned!' });
                onCloseDrawer?.();
            }
        });
    }

    const recordingUrl: string | undefined = note.otherAttributes?.recording?.url;
    const recordingFileName: string | undefined = note.otherAttributes?.recording?.fileName;
    if (recordingUrl) {
        actions.push({
            Icon: DownloadIcon,
            text: 'Download Recording',
            'data-type': 'download-recording-button',
            handler: () => {
                const link = document.createElement('a');
                link.href = recordingUrl;
                if (recordingFileName) {
                    link.download = recordingFileName;
                }
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.click();
            }
        });
    }

    if (model.canDelete(note)) {
        const deleteNote = () => {
            const destructiveRole = 'destructive';
            presentAlert({
                header: `Delete Note?`,
                message: `This note will be deleted and cannot be recovered.`,
                cssClass: 'drawer-alert',
                onDidDismiss: (e) => {
                    if (e.detail.role === destructiveRole) {
                        editNoteDrawer.close();
                    }
                },
                buttons: [{
                    text: 'Delete',
                    role: destructiveRole,
                    handler: () => {
                        model.deleteNote(note);
                        history.back();
                    }
                }, {
                    text: 'Cancel',
                    role: 'cancel'
                }]
            });
        };
        actions.push({
            Icon: DeleteIcon,
            role: 'destructive',
            text: `Delete Note`,
            handler: deleteNote,
            'data-type': 'delete-button'
        });
    }

    // Add created date as informational text
    // (but only if there are already actions in the menu)
    if (note.createdAt && actions.length > 0) {
        const createdDate = dayjs(note.createdAt);
        const formattedDate = createdDate.format('MM/DD/YY [at] h:mma');
        actions.push({
            text: `Created ${formattedDate}`,
            divider: true,
            noLines: true
        });
    }

    return (
        <>
            {actions.length > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: -8}}>
                    <div style={{flexGrow: 1}}>&nbsp;</div>
                    <div>
                        <Menu data-id="view-note-actions" actions={actions} />
                    </div>
                </div>
            )}
        </>
    );
};
